import { apiClient } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatConversation {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  lastMessage: string;
  lastMessageAt: string | null;
  unreadByUser: number;
  unreadByOwner: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  _id: string;
  conversationId: string;
  senderId: string;
  senderType: 'user' | 'owner';
  content: string;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConversationsResponse {
  conversations: ChatConversation[];
  total: number;
}

interface MessagesResponse {
  messages: ChatMessage[];
  total: number;
}

// ─── API Functions ────────────────────────────────────────────────────────────

export const fetchConversations = async (
  page = 1,
  limit = 20,
  search?: string,
): Promise<ConversationsResponse> => {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(limit),
  };
  if (search) params.search = search;
  const res = await apiClient.get('/chat/conversations', { params });
  return res.data;
};

export const fetchMyConversation =
  async (): Promise<ChatConversation> => {
    const res = await apiClient.get('/chat/conversation');
    return res.data;
  };

export const fetchMessages = async (
  conversationId: string,
  page = 1,
  limit = 50,
): Promise<MessagesResponse> => {
  const res = await apiClient.get(
    `/chat/conversations/${conversationId}/messages`,
    { params: { page: String(page), limit: String(limit) } },
  );
  return res.data;
};

export const sendMessage = async (
  conversationId: string,
  content: string,
): Promise<ChatMessage> => {
  const res = await apiClient.post(
    `/chat/conversations/${conversationId}/messages`,
    { content },
  );
  return res.data;
};

export const markConversationRead = async (
  conversationId: string,
): Promise<void> => {
  await apiClient.patch(`/chat/conversations/${conversationId}/read`);
};

export const fetchUnreadCount = async (): Promise<{
  unreadCount: number;
}> => {
  const res = await apiClient.get('/chat/unread-count');
  return res.data;
};

// ─── React Query Hooks ────────────────────────────────────────────────────────

export const useChatConversations = (search?: string) =>
  useQuery({
    queryKey: ['chat', 'conversations', search],
    queryFn: () => fetchConversations(1, 50, search),
    refetchInterval: 30_000,
  });

export const useMyConversation = () =>
  useQuery({
    queryKey: ['chat', 'my-conversation'],
    queryFn: fetchMyConversation,
  });

export const useChatMessages = (conversationId?: string) =>
  useQuery({
    queryKey: ['chat', 'messages', conversationId],
    queryFn: () => fetchMessages(conversationId!, 1, 100),
    enabled: !!conversationId,
    refetchInterval: 15_000,
  });

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: string;
    }) => sendMessage(conversationId, content),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['chat', 'messages', variables.conversationId],
      });
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['chat', 'my-conversation'] });
    },
  });
};

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      markConversationRead(conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
      qc.invalidateQueries({ queryKey: ['chat', 'my-conversation'] });
    },
  });
};

export const useChatUnreadCount = () =>
  useQuery({
    queryKey: ['chat', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
  });
